var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as _ from 'lodash';
import cx from 'classnames';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { AutoControlledComponent, childrenExist, createShorthandFactory, customPropTypes, doesNodeContainClick, commonPropTypes, isFromKeyboard, EventStack, } from '../../lib';
import Icon from '../Icon/Icon';
import Menu from '../Menu/Menu';
import Slot from '../Slot/Slot';
import { menuItemBehavior, submenuBehavior } from '../../lib/accessibility';
import { focusAsync } from '../../lib/accessibility/FocusZone';
import Ref from '../Ref/Ref';
/**
 * A menu item is an actionable navigation item within a menu.
 */
var MenuItem = /** @class */ (function (_super) {
    __extends(MenuItem, _super);
    function MenuItem() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.outsideClickSubscription = EventStack.noSubscription;
        _this.menuRef = React.createRef();
        _this.itemRef = React.createRef();
        _this.handleWrapperBlur = function (e) {
            if (!_this.props.inSubmenu && !e.currentTarget.contains(e.relatedTarget)) {
                _this.setState({ menuOpen: false });
            }
        };
        _this.actionHandlers = {
            performClick: function (event) { return _this.handleClick(event); },
            openMenu: function (event) { return _this.openMenu(event); },
            closeAllMenus: function (event) { return _this.closeAllMenus(event, false); },
            closeAllMenusAndFocusNextParentItem: function (event) { return _this.closeAllMenus(event, true); },
            closeMenu: function (event) { return _this.closeMenu(event); },
        };
        _this.outsideClickHandler = function (e) {
            if (!_this.state.menuOpen)
                return;
            if (!doesNodeContainClick(_this.itemRef.current, e) &&
                !doesNodeContainClick(_this.menuRef.current, e)) {
                _this.trySetState({ menuOpen: false });
            }
        };
        _this.performClick = function (e) {
            var _a = _this.props, active = _a.active, menu = _a.menu;
            if (menu) {
                if (doesNodeContainClick(_this.menuRef.current, e)) {
                    // submenu was clicked => close it and propagate
                    _this.setState({ menuOpen: false }, function () { return focusAsync(_this.itemRef.current); });
                }
                else {
                    // the menuItem element was clicked => toggle the open/close and stop propagation
                    _this.trySetState({ menuOpen: active ? !_this.state.menuOpen : true });
                    e.stopPropagation();
                }
            }
        };
        _this.handleClick = function (e) {
            _this.performClick(e);
            _.invoke(_this.props, 'onClick', e, _this.props);
        };
        _this.handleBlur = function (e) {
            _this.setState({ isFromKeyboard: false });
            _.invoke(_this.props, 'onBlur', e, _this.props);
        };
        _this.handleFocus = function (e) {
            _this.setState({ isFromKeyboard: isFromKeyboard() });
            _.invoke(_this.props, 'onFocus', e, _this.props);
        };
        _this.closeAllMenus = function (e, focusNextParent) {
            var _a = _this.props, menu = _a.menu, inSubmenu = _a.inSubmenu;
            var menuOpen = _this.state.menuOpen;
            if (menu && menuOpen) {
                _this.setState({ menuOpen: false }, function () {
                    if (!inSubmenu && (!focusNextParent || _this.props.vertical)) {
                        focusAsync(_this.itemRef.current);
                    }
                });
            }
        };
        _this.closeMenu = function (e) {
            var _a = _this.props, menu = _a.menu, inSubmenu = _a.inSubmenu;
            var menuOpen = _this.state.menuOpen;
            var shouldStopPropagation = inSubmenu || _this.props.vertical;
            if (menu && menuOpen) {
                _this.setState({ menuOpen: false }, function () {
                    if (shouldStopPropagation) {
                        focusAsync(_this.itemRef.current);
                    }
                });
                if (shouldStopPropagation) {
                    e.stopPropagation();
                }
            }
        };
        _this.openMenu = function (e) {
            var menu = _this.props.menu;
            var menuOpen = _this.state.menuOpen;
            if (menu && !menuOpen) {
                _this.setState({ menuOpen: true });
                _.invoke(_this.props, 'onActiveChanged', e, __assign({}, _this.props, { active: true }));
                e.stopPropagation();
                e.preventDefault();
            }
        };
        return _this;
    }
    MenuItem.prototype.componentDidMount = function () {
        this.updateOutsideClickSubscription();
    };
    MenuItem.prototype.componentDidUpdate = function () {
        this.updateOutsideClickSubscription();
    };
    MenuItem.prototype.componentWillUnmount = function () {
        this.outsideClickSubscription.unsubscribe();
    };
    MenuItem.prototype.renderComponent = function (_a) {
        var _this = this;
        var ElementType = _a.ElementType, classes = _a.classes, accessibility = _a.accessibility, rest = _a.rest, styles = _a.styles;
        var _b = this.props, children = _b.children, content = _b.content, icon = _b.icon, wrapper = _b.wrapper, menu = _b.menu, primary = _b.primary, secondary = _b.secondary, active = _b.active;
        var menuOpen = this.state.menuOpen;
        var menuItemInner = childrenExist(children) ? (children) : (React.createElement(Ref, { innerRef: this.itemRef },
            React.createElement(ElementType, __assign({ className: classes.root, onBlur: this.handleBlur, onFocus: this.handleFocus }, accessibility.attributes.anchor, rest, !wrapper && { onClick: this.handleClick }),
                icon &&
                    Icon.create(this.props.icon, {
                        defaultProps: { xSpacing: !!content ? 'after' : 'none' },
                    }),
                content)));
        var maybeSubmenu = menu && active && menuOpen ? (React.createElement(Ref, { innerRef: this.menuRef }, Menu.create(menu, {
            defaultProps: {
                accessibility: submenuBehavior,
                vertical: true,
                primary: primary,
                secondary: secondary,
                styles: styles.menu,
                submenu: true,
            },
        }))) : null;
        if (wrapper) {
            return Slot.create(wrapper, {
                defaultProps: __assign({ className: cx('ui-menu__item__wrapper', classes.wrapper) }, accessibility.attributes.root, accessibility.keyHandlers.root),
                overrideProps: function () { return ({
                    children: (React.createElement(React.Fragment, null,
                        menuItemInner,
                        maybeSubmenu)),
                    onClick: _this.handleClick,
                    onBlur: _this.handleWrapperBlur,
                }); },
            });
        }
        return menuItemInner;
    };
    MenuItem.prototype.updateOutsideClickSubscription = function () {
        var _this = this;
        this.outsideClickSubscription.unsubscribe();
        if (this.props.menu && this.state.menuOpen) {
            setTimeout(function () {
                _this.outsideClickSubscription = EventStack.subscribe('click', _this.outsideClickHandler);
            });
        }
    };
    MenuItem.displayName = 'MenuItem';
    MenuItem.className = 'ui-menu__item';
    MenuItem.propTypes = __assign({}, commonPropTypes.createCommon(), { accessibility: PropTypes.func, active: PropTypes.bool, disabled: PropTypes.bool, icon: customPropTypes.itemShorthand, iconOnly: PropTypes.bool, index: PropTypes.number, onClick: PropTypes.func, onFocus: PropTypes.func, pills: PropTypes.bool, pointing: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['start', 'end'])]), primary: customPropTypes.every([customPropTypes.disallow(['secondary']), PropTypes.bool]), secondary: customPropTypes.every([customPropTypes.disallow(['primary']), PropTypes.bool]), underlined: PropTypes.bool, vertical: PropTypes.bool, wrapper: PropTypes.oneOfType([PropTypes.node, PropTypes.object]), menu: customPropTypes.itemShorthand, menuOpen: PropTypes.bool, defaultMenuOpen: PropTypes.bool, onActiveChanged: PropTypes.func, inSubmenu: PropTypes.bool });
    MenuItem.defaultProps = {
        as: 'a',
        accessibility: menuItemBehavior,
        wrapper: { as: 'li' },
    };
    MenuItem.autoControlledProps = ['menuOpen'];
    return MenuItem;
}(AutoControlledComponent));
MenuItem.create = createShorthandFactory(MenuItem, 'content');
export default MenuItem;
